import Stack from "@mui/material/Stack";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import { getVendorRatingColor } from "app/redux/slice/supplierSlice";

const AddVendor = ({ value, onClose, handleSubmit }) => {
  const userData = useSelector((state) => state?.user);
  const mobileView = useMediaQuery("(max-width:575px)");

  const validationSchema = Yup.object({
    type: Yup.string().required("Please select type"),
    userId: Yup.string().nullable().required("Please select staff"),
    name: Yup.string()
      .min(3, "Name must be at least 3 characters")
      .max(50, "Name must be at most 50 characters")
      .required("Please enter name"),
    mobileNumber: Yup.string().required("Please enter phone number"),
  });

  return (
    <>
      <Formik
        initialValues={{
          type: value?.type || "",
          name: value?.name || "",
          email: value?.email || "",
          mobileNumber: value?.mobileNumber || "",
          address: value?.address || "",
          deliveryAddress: value?.deliveryAddress || "",
          brokerName: value?.brokerName || "",
          userId: value?.user?._id || null,
          gst: value?.gst || "",
          transportId: value?.transportId || "",
          transportName: value?.transportName || "",
          rating: value?.rating ?? null,
        }}
        validationSchema={validationSchema}
        onSubmit={(data, { setSubmitting }) => {
          setSubmitting(true);
          setTimeout(() => {
            handleSubmit(data);
            setSubmitting(false);
          }, 1500);
        }}
      >
        {({
          isSubmitting,
          touched,
          errors,
          values,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={touched.type && Boolean(errors.type)}
                >
                  <InputLabel id="type-label">Create For*</InputLabel>
                  <Select
                    name="type"
                    labelId="type-label"
                    id="type"
                    value={values.type}
                    label="Create For"
                    onChange={(e) => {
                      setFieldValue("type", e.target.value);
                      setFieldValue("userId", null);
                    }}
                    onBlur={handleBlur}
                  >
                    {["Office", "Mill", "Knitter"].map((val) => {
                      return <MenuItem value={val}>{val}</MenuItem>;
                    })}
                  </Select>
                  {touched.type && errors.type && (
                    <FormHelperText>{errors.type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="name" label="Name*" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="email" label="Email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  fullWidth
                  name="mobileNumber"
                  label="Phone*"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="gst" label="GST" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <JumboAppTextField fullWidth name="brokerName" label="Broker" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl
                  fullWidth
                  error={touched.userId && Boolean(errors.userId)}
                >
                  <InputLabel id="type-label">Belongs To Staff*</InputLabel>
                  <Select
                    name="userId"
                    labelId="type-label"
                    id="userId"
                    value={values.userId}
                    label="Belongs To Staff"
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {userData?.list
                      ?.filter?.((val) => val.type === values.type)
                      ?.map((val) => {
                        return (
                          <MenuItem key={val._id} value={val._id}>
                            {val.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                  {touched.userId && errors.userId && (
                    <FormHelperText>{errors.userId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <JumboAppTextField
                  fullWidth
                  name="transportName"
                  label="Transport Name"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <JumboAppTextField
                  fullWidth
                  name="transportId"
                  label="Transport Id"
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <JumboAppTextField fullWidth name="address" label="Address" />
              </Grid>
              <Grid item xs={12} sm={12}>
                <JumboAppTextField
                  fullWidth
                  name="deliveryAddress"
                  label="Delivery Address"
                />
              </Grid>
              <Grid item xs={12} sm={12} sx={{ mb: 4 }}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Rating
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Rating
                    name="rating"
                    max={5}
                    value={values.rating ?? null}
                    // Clicking the selected star again clears it back to null,
                    // which is how a vendor becomes "not rated" again.
                    onChange={(e, newValue) => setFieldValue("rating", newValue)}
                    sx={{ color: getVendorRatingColor(values.rating) }}
                  />
                  <Typography variant="body2" color="text.secondary" mb={0}>
                    {values.rating
                      ? `${values.rating} / 5`
                      : "Not rated — click a star to rate"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Stack
              spacing={{ xs: 1, sm: 2 }}
              direction="row"
              justifyContent="flex-end"
            >
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                size="large"
                // sx={{ mb: 3 }}
                fullWidth={mobileView}
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                type="submit"
                variant="contained"
                size="large"
                sx={{ mb: 3 }}
                loading={isSubmitting}
                loadingIndicator={<CircularProgress size={20} />}
                fullWidth={mobileView}
              >
                Save
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddVendor;
